import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { CookieConsent } from "@/components/CookieConsent";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { ScrollToTop } from "./components/ScrollToTop";
import { usePageTracking } from "./hooks/usePageTracking";

// Eagerly loaded: Landing page (critical path)
import Landing from "./pages/Landing";

// Lazy-loaded pages
const Pricing = lazy(() => import("./pages/Pricing"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ProjectsList = lazy(() => import("./pages/ProjectsList"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductWorkspace = lazy(() => import("./pages/ProductWorkspace"));
const MemoryIndex = lazy(() => import("./pages/MemoryIndex"));
const RequirementsReadiness = lazy(() => import("./pages/RequirementsReadiness"));
const ProjectReadiness = lazy(() => import("./pages/ProjectReadiness"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const NewRunWizard = lazy(() => import("./pages/NewRunWizard"));
const RunProgress = lazy(() => import("./pages/RunProgress"));
const RunResults = lazy(() => import("./pages/RunResults"));
const ProductMemory = lazy(() => import("./pages/ProductMemory"));
const ProjectContext = lazy(() => import("./pages/ProjectContext"));
const SettingsLayout = lazy(() => import("./pages/settings/SettingsLayout"));
const SettingsAccount = lazy(() => import("./pages/settings/SettingsAccount"));
const SettingsWorkspace = lazy(() => import("./pages/settings/SettingsWorkspace"));
const SettingsRoles = lazy(() => import("./pages/settings/SettingsRoles"));
const SettingsBilling = lazy(() => import("./pages/settings/SettingsBilling"));
const SettingsPrivacy = lazy(() => import("./pages/settings/SettingsPrivacy"));
const SettingsIntegrations = lazy(() => import("./pages/settings/SettingsIntegrations"));
const SettingsDeveloper = lazy(() => import("./pages/settings/SettingsDeveloper"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const DocsLayout = lazy(() => import("./pages/DocsLayout"));
const DocsIndex = lazy(() => import("./pages/DocsIndex"));
const DocsSection = lazy(() => import("./pages/DocsSection"));
const About = lazy(() => import("./pages/About"));
const Security = lazy(() => import("./pages/Security"));
const DPA = lazy(() => import("./pages/DPA"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ContactSales = lazy(() => import("./pages/ContactSales"));
const PersonaLibrary = lazy(() => import("./pages/PersonaLibrary"));
const RunInsights = lazy(() => import("./pages/RunInsights"));
const ClarityInsights = lazy(() => import("./pages/ClarityInsights"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminDocs = lazy(() => import("./pages/AdminDocs"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminDebug = lazy(() => import("./pages/AdminDebug"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const Blog = lazy(() => import("./pages/Blog"));
const BetaAccess = lazy(() => import("./pages/BetaAccess"));
const AdminBeta = lazy(() => import("./pages/AdminBeta"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogEditor = lazy(() => import("./pages/BlogEditor"));
const Changelog = lazy(() => import("./pages/Changelog"));
const ChangelogEditor = lazy(() => import("./pages/ChangelogEditor"));
const AdminChangelog = lazy(() => import("./pages/AdminChangelog"));
const AdminDistillationSignals = lazy(() => import("./pages/AdminDistillationSignals"));
const BackendDebug = lazy(() => import("./pages/BackendDebug"));
const DebugSupabaseExecution = lazy(() => import("./pages/DebugSupabaseExecution"));
const DebugOverview = lazy(() => import("./pages/DebugOverview"));
const DebugEngines = lazy(() => import("./pages/DebugEngines"));
const DebugSchemas = lazy(() => import("./pages/DebugSchemas"));
const DebugCredits = lazy(() => import("./pages/DebugCredits"));

const AppLayout = lazy(() => import("./components/layout/AppLayout").then(m => ({ default: m.AppLayout })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
}

// Route that redirects authenticated users
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Redirect logged-in users from landing to dashboard
function AuthAwareLanding() {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner />;
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Landing />;
}

function AppRoutes() {
  usePageTracking();
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<AuthAwareLanding />} />
        <Route path="/home" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/docs" element={<DocsLayout />}>
          <Route index element={<DocsIndex />} />
          <Route path=":section" element={<DocsSection />} />
        </Route>
        <Route path="/about" element={<About />} />
        <Route path="/security" element={<Security />} />
        <Route path="/dpa" element={<DPA />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/contact-sales" element={<ContactSales />} />
        <Route path="/beta" element={<BetaAccess />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/new" element={<BlogEditor />} />
        <Route path="/blog/:slug/edit" element={<BlogEditor />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/changelog/new" element={<ChangelogEditor />} />
        <Route path="/changelog/:slug/edit" element={<ChangelogEditor />} />
        <Route
          path="/auth"
          element={
            <AuthRoute>
              <Auth />
            </AuthRoute>
          }
        />

        {/* App routes (authenticated) */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard/products" element={<ProductsPage />} />
          <Route path="/dashboard/products/:productId" element={<ProductWorkspace />} />
          <Route path="/dashboard/runs" element={<RunInsights />} />
          <Route path="/dashboard/reviews" element={<RequirementsReadiness />} />
          <Route path="/dashboard/memory" element={<MemoryIndex />} />
          <Route path="/dashboard/team" element={<TeamPage />} />
          <Route path="/dashboard/projects" element={<ProjectsList />} />
          <Route path="/dashboard/projects/:projectId" element={<ProjectDetails />} />
          <Route path="/dashboard/projects/:projectId/readiness" element={<ProjectReadiness />} />
          <Route path="/dashboard/new-run" element={<NewRunWizard />} />
          <Route path="/dashboard/runs/:runId" element={<RunProgress />} />
          <Route path="/dashboard/runs/:runId/results" element={<RunResults />} />
          <Route path="/dashboard/projects/:projectId/context" element={<ProjectContext />} />
          <Route path="/dashboard/products/:productId/memory" element={<ProductMemory />} />
          <Route path="/personas" element={<PersonaLibrary />} />
          <Route path="/dashboard/insights/runs" element={<RunInsights />} />
          <Route path="/dashboard/insights/clarity" element={<ClarityInsights />} />
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<SettingsAccount />} />
            <Route path="workspace" element={<SettingsWorkspace />} />
            <Route path="roles" element={<SettingsRoles />} />
            <Route path="billing" element={<SettingsBilling />} />
            <Route path="privacy" element={<SettingsPrivacy />} />
            <Route path="integrations" element={<SettingsIntegrations />} />
            <Route path="developer" element={<SettingsDeveloper />} />
          </Route>
        </Route>

        {/* Admin routes — shared layout with tab nav */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="beta" element={<AdminBeta />} />
          <Route path="docs" element={<AdminDocs />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="changelog" element={<AdminChangelog />} />
          <Route path="signals" element={<AdminDistillationSignals />} />
          <Route path="debug" element={<AdminDebug />}>
            <Route index element={<DebugOverview />} />
            <Route path="backend" element={<BackendDebug />} />
            <Route path="execution" element={<Navigate to="/admin/debug/supabase-execution" replace />} />
            <Route path="supabase-execution" element={<DebugSupabaseExecution />} />
            <Route path="engines" element={<DebugEngines />} />
            <Route path="schemas" element={<DebugSchemas />} />
            <Route path="credits" element={<DebugCredits />} />
          </Route>
        </Route>

        {/* Legacy debug redirects */}
        <Route path="/debug" element={<Navigate to="/admin/debug" replace />} />
        <Route path="/debug/backend" element={<Navigate to="/admin/debug/backend" replace />} />
        <Route path="/debug/execution" element={<Navigate to="/admin/debug/supabase-execution" replace />} />
        <Route path="/debug/supabase-execution" element={<Navigate to="/admin/debug/supabase-execution" replace />} />

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <CookieConsent />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <SubscriptionProvider>
            <AppRoutes />
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;